import React, { useState } from "react";
import { Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useJobs } from "@/contexts/JobsContext";

// Face-detection kickoff button (admin only). Enqueues a background job
// that pulls each realtor photo, runs OpenCV Haar face-detection, and
// flags placeholder/logo images. Job progress is tracked via the global
// jobs banner (`photo_check` type).
//
// Behaviour: ALWAYS runs a full recheck when clicked (recheck=true).
// Earlier default of recheck=false meant subsequent clicks skipped every
// already-checked realtor and the user reported "the button only searches
// a handful of profiles". Rechecking every click matches the button label
// and gives the user predictable behaviour.
//
// When `selectedIds` is a non-empty array the button re-scopes to
// exactly those realtors and the label flips to "Check N photos".
export default function CheckPhotosButton({ selectedIds = [] }) {
  const [busy, setBusy] = useState(false);
  const { track: trackJob } = useJobs();
  const scoped = Array.isArray(selectedIds) && selectedIds.length > 0;

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        "/realtors/check-profile-photos",
        scoped ? selectedIds : null,
        { params: { recheck: true } },
      );
      if (data?.status === "empty") {
        toast.message(
          scoped
            ? "None of the selected realtors have a photo URL to check"
            : "No realtor photos to check",
          {
            description: scoped
              ? "Try selecting realtors that have a photo on file."
              : "There are no realtors with a photo_url in the current scope.",
          },
        );
      } else if (data?.job_id) {
        trackJob(data.job_id, {
          type: "photo_check",
          label: scoped
            ? `Face-detect ${data.targets} selected photos`
            : `Face-detect ${data.targets} realtor photos`,
        });
        toast.success(
          scoped
            ? `Checking ${data.targets} selected photos…`
            : `Checking ${data.targets} photos…`,
          {
            description:
              "You'll see a logo flag on realtors whose photo is a brokerage logo or broken.",
          },
        );
      }
    } catch (e) {
      toast.error("Failed to start photo check", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={start}
      disabled={busy}
      className="h-8 px-2.5 text-xs"
      data-testid="check-photos-button"
      title={
        scoped
          ? `Face-detect the ${selectedIds.length} selected realtor photos`
          : "Detect brokerage logos & broken placeholders across all realtor profile pictures"
      }
    >
      {busy ? (
        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
      ) : (
        <ImageIcon className="h-3.5 w-3.5 mr-1.5" />
      )}
      {scoped ? `Check ${selectedIds.length} photos` : "Check photos"}
    </Button>
  );
}
